import { normalizeRiskLevel, riskLevelsToCodes } from "./riskLevel";
import { vulnHitToView } from "./vulnContract";

/**
 * `DcHardware` ↔ 前端硬體檢視模型（票 21）。
 *
 * 純轉換、不依賴 React —— 與 `rackContract.js`／`roleContract.js` 同一種角色：
 * 頁面不該散落 `h.host_name ?? h.name` 這類 snake/camel 混寫，錯一個欄位就是空白格。
 *
 * ## 契約來源
 * **2026-08-18 smoke 實測定版**（ADR-0004）。8/12 時 `GetAll` 還是 HTTP 500，
 * 8/18 跑通（2666 筆），三個查表鍵的實際值都收到了：
 *   `*_risk_level` → `"嚴重"`（前端認得）
 *   `status_name`  → `"運作中"`／`"已停用"`（前端全站用的是「運行中」，見下）
 *   `position_u`   → **`"U12-13"`**（文件只寫 string，實際帶 U 前綴與區間）
 * 最後一項推翻了原本照文件寫的猜測，也正是 ADR-0004 存在的理由。
 *
 * ## 唯讀欄位（後端算，前端不要送也不要自己算 —— ADR-0007）
 * `eos_risk_level`／`eol_risk_level`／`cve_risk_level`／`kev_risk_level`、
 * `cve_count`／`kev_count`、`vulnerability_hits`、`is_under_review`／
 * `approval_request_no`、`created_on`／`updated_on`。
 *
 * ## 兩個踩得到的地雷
 * 1. **`position_u` 是 `"U12-13"`** —— 見下方 `parseUPosition` 的說明。
 * 2. **狀態一字之差**：後端回「運**作**中」，前端全站（`StatusTag`、
 *    `ApprovalFlow` 的顏色表、種子）寫的是「運**行**中」。對不上就是整欄
 *    掉回預設顏色，不報錯也沒人發現 —— 所以下面有 alias 表 ＋ `console.warn`。
 */

/* ————— 狀態（查表鍵） ————— */

/** 後端 `status_name` → 前端全站在用的字 */
const STATUS_ALIASES = {
  運作中: "運行中",
  運行中: "運行中",
  維修中: "維修中",
  已停用: "停用",
  停用: "停用",
  閒置: "閒置",
};

const warnedStatuses = new Set();
export function normalizeHardwareStatus(raw) {
  const key = String(raw == null ? "" : raw).trim();
  if (!key) return "";
  const hit = STATUS_ALIASES[key];
  if (hit) return hit;
  if (!warnedStatuses.has(key)) {
    warnedStatuses.add(key);
    console.warn(
      `[hardware] 狀態「${key}」不在對照表裡 —— StatusTag 與 ApprovalFlow 都拿它當`,
      `顏色的查表鍵,對不上就是安靜地掉回預設樣式。`,
      `目前認得:${Object.keys(STATUS_ALIASES).join("／")}。`,
      "請補進 helpers/hardwareContract.js 的 STATUS_ALIASES。"
    );
  }
  return key; // 保留原值:寧可看到一個沒見過的字,也不要整欄變空白
}

/* ————— U 位（`position_u`） ————— */

/**
 * `position_u` 的實際形狀 —— **2026-08-18 smoke 實測定版**（ADR-0004）。
 *
 * 文件只寫 `string`；實測回的是 **`"U12-13"`**：帶 `U` 前綴，而且**區間就寫在
 * 這一個欄位裡**（列表列 DTO 沒有 `occupied_u`，佔幾格只能從這裡推）。
 *
 * 照文件猜「就是個字串」會踩到三個地方，而且三個都是**不報錯但錯**：
 *   1. 列表的位置欄本來就在前面補一個 `U` → 畫面顯示 `UU12-13`
 *   2. `uSlots.uRange()` 做 `Number("U12")` → `NaN`，而 **NaN 的比較永遠是
 *      false** → U 位衝突偵測永遠偵測不到衝突（送出時才被後端擋，或根本不擋）
 *   3. 佔用格數退回預設的 1U → U 位圖畫錯格數
 *
 * 拆不出來時回 `null` 不回 `NaN`：呼叫端擋得掉 null，擋不掉 NaN。
 */
export function parseUPosition(raw) {
  const text = String(raw == null ? "" : raw).trim();
  // 連字號有三種寫法（半形 -、全形 –、波浪 ~），前綴 U 大小寫都收
  const m = text.match(/^[Uu]?\s*(\d+)\s*(?:[-–~]\s*[Uu]?\s*(\d+))?$/);
  if (!m) return { start: null, end: null };
  const start = Number(m[1]);
  return { start, end: m[2] === undefined ? start : Number(m[2]) };
}

/** 起點（＋佔用格數）→ 後端在用的字串格式。單格不加區間，與實測一致。 */
export function formatUPosition(uPosition, uSize) {
  const { start, end } = parseUPosition(uPosition);
  if (start === null) return "";
  // 已經帶區間的原值優先（使用者可能直接輸入 "U12-13"），否則用格數推
  const last = end !== start ? end : start + (Number(uSize) || 1) - 1;
  return last > start ? `U${start}-${last}` : `U${start}`;
}

/* ————— 讀：DTO → 檢視模型 ————— */

/**
 * `DcHardwareListItemDto`／`DcHardwareDto` → 列表與表單看得懂的形狀。
 *
 * 兩個 DTO 的共同欄位同名，所以共用一支；詳情頁多出來的欄位（規格、虛擬化、
 * 弱掃）原樣留在 `...h` 裡，需要時再各自取。
 */
export function hardwareToView(h) {
  if (!h) return h;
  const uPos = parseUPosition(h.position_u ?? h.uPosition);
  return {
    ...h,
    id: h.id,
    // 稿上這一欄叫「資產編號/Host Name」—— 主鍵欄是資產編號,序號另外放
    code: h.asset_no,
    // `assetCode`／`keyBiz` 是**機櫃表單就在用的名字**(`rackContract`)，而硬體表單
    // 也照那套寫。兩邊名字對不上的後果不是空白格，是**送出去的值是錯的**：
    //   `asset_no` 收的是表單的「硬體編號」(`code`)，使用者填的「資產編號」丟掉
    //   `is_key_business` 讀 `starred`，而表單只有 `keyBiz` → **永遠送 false**
    // 兩個名字都給:列表與 U 位圖沿用 `code`／`starred`，表單走 `assetCode`／`keyBiz`。
    assetCode: h.asset_no,
    name: h.host_name,
    serialNo: h.hardware_no,
    starred: !!h.is_key_business,
    keyBiz: h.is_key_business ? "啟用" : "不啟用",
    brand: h.brand,
    brandId: h.brand_id,
    model: h.model,
    modelId: h.model_id,
    // 位置三段各自留著,列表自己組「機房/機櫃/U位」
    roomId: h.room_id,
    roomName: h.room_name,
    rackId: h.rack_id,
    // 前端全站(`uSlots`／`HardwareForm`／`HardwareDetail`／機櫃頁)叫它 `cabinetId`,
    // 兩個名字都給:契約側讀 `rackId`,既有程式讀 `cabinetId`,不必為了改名重寫 U 位邏輯。
    cabinetId: h.rack_id ?? h.cabinetId,
    cabinetCode: h.rack_no,
    // 起點以字串給:`uSlots` 與表單從種子時代就吃這個形狀
    uPosition: uPos.start === null ? "" : String(uPos.start),
    uEnd: uPos.end,
    // `occupied_u` 只在編輯頁 DTO 裡。列表列沒有 —— 這時佔幾格從 `position_u`
    // 的區間推（"U12-13" 就是 2 格）；都拿不到才退回 `...h` 帶進來的值。
    uSize: h.occupied_u
      ?? (uPos.start !== null && uPos.end !== uPos.start ? uPos.end - uPos.start + 1 : undefined)
      ?? h.uSize
      ?? (uPos.start !== null ? 1 : undefined),
    ip: h.ip_address ?? h.ip,
    mac: h.mac_address ?? h.mac,
    // 風險:日期切到 YYYY-MM-DD,等級照後端(ADR-0007 —— 前端不自己算)
    eos: dateOnly(h.eos_date),
    eol: dateOnly(h.eol_date),
    endOfSale: dateOnly(h.end_of_sale_date),
    eosLevel: normalizeRiskLevel(h.eos_risk_level),
    eolLevel: normalizeRiskLevel(h.eol_risk_level),
    cveLevel: normalizeRiskLevel(h.cve_risk_level),
    kevLevel: normalizeRiskLevel(h.kev_risk_level),
    // 0 是「掃過、沒命中」,缺欄是「不知道」—— 兩者在畫面上不該長得一樣
    cveCount: countOrNull(h.cve_count),
    kevCount: countOrNull(h.kev_count),
    // 命中明細(票 uat-round3/01;DCK-BUG-001):GetById 才有 `vulnerability_hits`,
    // 列表列沒有 → 空陣列(表格吃得下),已是前端形狀的列(merge 進來的)原樣留。
    // 2026-09-07 smoke:GetById/8 有 46 筆命中但 `cve_date`／`kev_date` 是 null ——
    // 那是後端缺陷(另行回報),這裡只映射,不從命中列推一個日期出來假裝有。
    cveDate: dateOnly(h.cve_date) || h.cveDate || "",
    kevDate: dateOnly(h.kev_date) || h.kevDate || "",
    cveHits: Array.isArray(h.vulnerability_hits)
      ? h.vulnerability_hits.map(vulnHitToView)
      : (h.cveHits || []),
    manager: h.manager_customer_name,
    managerId: h.manager_customer_id,
    // ⚠️ 業務類別／資產類別**只在編輯頁 DTO 裡**,列表列沒有 ——
    // 用 `??` 而不是直接指派,否則列表的每一列都會被 undefined 蓋掉
    // (`...h` 帶進來的 mock-only 值就這樣消失,而畫面只是變空白)。
    bizType: h.business_category_name ?? h.bizType,
    bizTypeId: h.business_category_id,
    assetType: h.asset_category_name ?? h.assetType,
    assetTypeId: h.asset_category_id,
    status: normalizeHardwareStatus(h.status_name),
    // 沒送審就不要有這兩個鍵 —— 有值等於畫面上會長出審核狀態
    approvalStatus: h.is_under_review ? "pending" : undefined,
    docNo: h.approval_request_no || undefined,

    /* ————— 編輯頁 DTO 才有（GetById）。列表列沒有這些鍵 ——
     * 用 `??` 接 mock-only／已經是表單名的值，避免列表每一列都被空字串蓋掉。 */
    firmwareVersion: h.firmware_version ?? h.firmwareVersion ?? "",
    interfaceCount: h.interface_count ?? h.interfaceCount ?? "",
    powerCount: h.power_count ?? h.powerCount ?? "",
    cpuCores: h.cpu_core_count ?? h.cpuCores ?? "",
    ramGb: h.ram_gb ?? h.ramGb ?? "",
    storageTb: h.disk_capacity_tb ?? h.storageTb ?? "",
    // 作業系統／應用系統(票 api-contract-alignment-20260907/01;2026-09-08 smoke 定版):
    // 後端已改成軟體關聯陣列 `operating_systems[]`／`application_systems[]`(元素 { id, software_no, name, version })
    // 與 `*_ids[]`,單數字串欄位不存在了。表單那兩格維持文字框(不改畫面),這裡把陣列攤成
    // 「名稱(沒名稱用軟體編號)」頓號串給它顯示;id 陣列另外帶著,寫回時送的是它。
    // 舊形狀(單數字串:mock 種子、舊契約)照舊可讀。
    os: Array.isArray(h.operating_systems)
      ? softwareRelationText(h.operating_systems)
      : (h.operating_system ?? h.os ?? ""),
    appSystems: Array.isArray(h.application_systems)
      ? softwareRelationText(h.application_systems)
      : (h.application_system ?? h.appSystems ?? ""),
    operatingSystemIds: idList(h.operating_system_ids ?? h.operatingSystemIds),
    applicationSystemIds: idList(h.application_system_ids ?? h.applicationSystemIds),
    // 解不出的 position_u(實測有 "3C03" 這種格式)原字串留著,寫回時原樣送,不清掉
    positionRaw: typeof h.position_u === "string" ? h.position_u : (h.positionRaw ?? ""),
    description: h.remark ?? h.description ?? "",
    propertyNo: h.property_no ?? h.propertyNo ?? "",
    purchaseDate: dateOnly(h.purchase_date) || h.purchaseDate || "",
    purchaseAmount: h.purchase_amount ?? h.purchaseAmount ?? "",
    currency: h.currency_code ?? h.currency ?? "TWD",
    mountDate: dateOnly(h.mounted_date) || h.mountDate || "",
    // WF_v3 票 21 新欄位:結束攤提日(⚠️ 契約外 mock-only,後端 DTO 待補)
    amortEndDate: dateOnly(h.amort_end_date) || h.amortEndDate || "",
    vmBrand: h.virtual_brand ?? h.vmBrand ?? "",
    vmCount: h.virtual_machine_count ?? h.vmCount ?? "",
    deployCpuCores: h.virtual_cpu_core_count ?? h.deployCpuCores ?? "",
    deployRamGb: h.virtual_ram_gb ?? h.deployRamGb ?? "",
    deployStorageTb: h.virtual_disk_capacity_tb ?? h.deployStorageTb ?? "",
    changeLog: h.change_log ?? h.changeLog ?? [],
    scan: {
      date: dateOnly(h.vulnerability_scan_date) || (h.scan && h.scan.date) || "",
      nextDate: dateOnly(h.next_vulnerability_scan_date) || (h.scan && h.scan.nextDate) || "",
      files: (h.scan && h.scan.files) || [],
      rows: (h.scan && h.scan.rows) || [],
    },
  };
}

/* ————— 寫：表單 → DTO ————— */

/**
 * 只送後端**收**的欄位。唯讀那批（見檔頭）不送 —— 送了也是被忽略，
 * 但留在 payload 會讓「誰算的」變得模糊，而 ADR-0007 的裁示正是
 * 「後端算得出來的值前端不自己再算一套」。
 */
/**
 * 「主檔只有名稱、沒有 id」時要出聲（ADR-0004；`rackToDto` 同一種防線）。
 *
 * 這四個 id 送 0 的後果是**安靜的錯值**：後端要嘛存成 0、要嘛驗證失敗，
 * 而畫面上一切正常 —— 票 20 的 `business_category_id` 就是這樣送了一整輪。
 */
const warnedMissingIds = new Set();
function warnMissingId(kind, field, name) {
  if (warnedMissingIds.has(kind)) return;
  warnedMissingIds.add(kind);
  console.warn(
    `[hardware] ${kind}只有名稱「${name}」沒有 id,${field} 將送 0 ——`,
    "表單的下拉要吃主檔(業務／資產類別走 Common 票 27,廠牌／型號走 DcBrand 票 32),",
    "不能用寫死的中文選項當主鍵。對照 helpers/hardwareContract.js"
  );
}

export function hardwareToDto(v, { id } = {}) {
  if (!v.bizTypeId && v.bizType) warnMissingId("業務類別", "business_category_id", v.bizType);
  if (!v.assetTypeId && v.assetType) warnMissingId("資產類別", "asset_category_id", v.assetType);
  if (!v.brandId && v.brand) warnMissingId("廠牌", "brand_id", v.brand);
  if (!v.modelId && v.model) warnMissingId("型號", "model_id", v.model);
  const out = {
    // `code` 是硬體這條鏈從種子到列表一路在用的名字;`assetCode` 是表單一度自己
    // 另開的一格(機櫃表單有 `code`=機櫃編號 與 `assetCode`=資產編號兩個,硬體被
    // 照抄了過來,但硬體只有一個編號)。表單已經收斂回 `code`,這裡兩個都收,
    // 是為了讓還帶著舊鍵的呼叫端不會靜靜送出空字串。
    asset_no: v.code ?? v.assetCode ?? "",
    host_name: v.name || "",
    hardware_no: v.serialNo || "",
    // 表單存的是「啟用」/「不啟用」,列表檢視模型存的是布林 —— 同上,兩個都收
    is_key_business: v.keyBiz !== undefined ? v.keyBiz === "啟用" : !!v.starred,
    business_category_id: num(v.bizTypeId),
    asset_category_id: num(v.assetTypeId),
    manager_customer_id: num(v.managerId),
    property_no: v.propertyNo || "",
    // id 是後端要的鍵,名稱是顯示用的 —— 兩個都送(票 20 只送名字送成 0 的教訓)
    brand: v.brand || "",
    brand_id: num(v.brandId),
    model: v.model || "",
    model_id: num(v.modelId),
    room_id: num(v.roomId),
    // 表單欄位叫 `cabinetId`(全站沿用的名字),契約叫 `rack_id` —— 兩個都收,
    // 只讀 `rackId` 的話表單送出來的機櫃會變成 0,而 mock 與後端都會擋成 400
    rack_id: num(v.rackId ?? v.cabinetId),
    // 實測格式 "U12-13";使用者沒填而原值解不出(如 "3C03")→ 原樣送回,不覆寫成空
    position_u: formatUPosition(v.uPosition, v.uSize) || v.positionRaw || "",
    occupied_u: num(v.uSize),
    mac_address: v.mac || "",
    firmware_version: v.firmwareVersion || "",
    // 作業系統／應用系統:契約已是軟體 id 陣列(見 hardwareToView 同段註解)。文字框的內容契約外、
    // 靜默排除;沒有既有關聯送空陣列(不送 undefined,後端 additionalProperties 才不會缺鍵)
    operating_system_ids: idList(v.operatingSystemIds),
    application_system_ids: idList(v.applicationSystemIds),
    purchase_date: v.purchaseDate || null,
    purchase_amount: num(v.purchaseAmount),
    currency_code: v.currency || "TWD",
    mounted_date: v.mountDate || null,
    // 表單的「備註」欄位名是 `description`(AutoFromModelCard),契約叫 `remark`。
    // 只讀 `v.remark` 的話,編輯時送出去的是**編輯前那筆的舊值**(從 `...h` 帶進來的),
    // 使用者改了備註等於沒改 —— 與資產編號那格同一型的漏接(票 42 第五節)。
    remark: v.description ?? v.remark ?? "",
    active: v.status ? v.status !== "停用" : true,

    /* ————— 規格（「由廠牌型號」卡：選型號自動帶入，可人工改） ————— */
    // 票 42 第五節:這一整段以前**沒有送出去**。表單收了、8/19 還做成自動帶入,
    // 然後整批丟掉 —— 帶得漂亮卻存不進去,比原本空著更誤導。
    interface_count: num(v.interfaceCount),
    power_count: num(v.powerCount),
    cpu_core_count: num(v.cpuCores),
    ram_gb: gbOf(v.ramGb, v.ramUnit),
    disk_capacity_tb: tbOf(v.storageTb, v.storageUnit),

    /* ————— 生命週期日期（同上，之前也沒送） ————— */
    eos_date: v.eos || null,
    eol_date: v.eol || null,
    end_of_sale_date: v.endOfSale || null,

    /* ————— 部署資源規格（「可由 SNMP／Agent Discover 回填」那張卡，整張都沒送） ————— */
    virtual_brand: v.vmBrand || "",
    virtual_machine_count: num(v.vmCount),
    virtual_cpu_core_count: num(v.deployCpuCores),
    virtual_ram_gb: gbOf(v.deployRamGb, v.deployRamUnit),
    virtual_disk_capacity_tb: tbOf(v.deployStorageTb, v.deployStorageUnit),

    /* ————— 弱掃日期（檔案本身沒有上傳端點，見票 42 第九項） ————— */
    vulnerability_scan_date: (v.scan && v.scan.date) || null,
    next_vulnerability_scan_date: (v.scan && v.scan.nextDate) || null,
    /**
     * ⚠️ 契約外（mock-only）—— `DcHardwareDto` **沒有**這些欄位，真後端會忽略。
     *
     * 留著是因為畫面上這些功能是真的存在的，而後端目前接不住：
     *   `ip`／`parentIp`／`secondaryIps`／`subnetMask`／`gateway`／`dns`
     *     編輯 DTO **完全沒有 IP 欄位**（列表列卻有 `ip_address`）——
     *     IP 大概落在 `DcIpAddress`，而那支連 path 都還沒有（票 25）
     *   `assetType`／`bizType` 名稱：DTO 只收 id，而 id 的來源是 `Common`（票 27）
     *   `lifecycle`／`isSpare`／`spareCode`：兩段式新增與替代機，後端沒有對應欄位
     *
     * 對稱於 mock 投影在**讀**的方向做的同一件事（`mock/index.js` 的
     * `hardwareRowToDto`）。等後端補上就把這一段拿掉 —— 缺口逐項記在票 21。
     */
    ip: v.ip || "",
    parentIp: v.parentIp || "",
    secondaryIps: v.secondaryIps || [],
    amortEndDate: v.amortEndDate || "", // WF_v3 票 21(契約外,同上)
    subnetMask: v.subnetMask || "",
    gateway: v.gateway || "",
    dns: v.dns || "",
    iface: v.iface || "",
    assetType: v.assetType || "",
    bizType: v.bizType || "",
    lifecycle: v.lifecycle,
    isSpare: !!v.isSpare,
    spareCode: v.spareCode || "",
  };
  if (id != null) out.id = num(id);
  return out;
}

/* ————— 篩選 → 查詢參數 ————— */

/** 前端的風險種類 key → 契約的代號 */
const KIND_TO_RISK_TYPE = { eol: "EOL", eos: "EOS", cve: "CVE", kev: "KEV" };
/** 時間窗 key → 月數（契約只收 3／6／12） */
const WINDOW_TO_MONTHS = { "3m": 3, "6m": 6, "1y": 12 };

/** 看板「已過期」：結束日=今天。起日給一個夠早的錨，後端區間才收得到歷史過期件。 */
export function expiredDateRange(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return { from: "2000-01-01", to: `${y}-${m}-${d}` };
}

/**
 * 風險條件 → `GetAll` 的查詢參數（ADR-0007：**送給後端篩，不在前端過濾**）。
 *
 * 「沒有條件就不要有那個 key」是這支的重點：空陣列會被 axios 序列化成
 * `riskTypes=`，後端很可能當成「一個空字串的種類」而篩掉全部 ——
 * 畫面上看起來是「這個條件沒資料」，不會有任何錯誤訊息。
 */
export function hardwareQuery(filter = {}, extra = {}) {
  const q = { ...extra };
  const kinds = (filter.kinds || []).map((k) => KIND_TO_RISK_TYPE[k]).filter(Boolean);
  if (kinds.length) q.riskTypes = kinds;

  const levels = riskLevelsToCodes(filter.levels);
  if (levels.length) q.riskLevels = levels;

  // 契約：riskStartDate／riskEndDate **優先於** riskMonths。兩組都送會讓
  // 「哪個生效」取決於後端實作 —— 自訂起訖在場時就不要送月數。
  // window=expired：看板「已過期」—— 結束日=今天，不是未來 6 個月窗。
  if (filter.from && filter.to) {
    q.riskStartDate = filter.from;
    q.riskEndDate = filter.to;
  } else if (filter.window === "expired") {
    const range = expiredDateRange();
    q.riskStartDate = range.from;
    q.riskEndDate = range.to;
  } else if (WINDOW_TO_MONTHS[filter.window]) {
    q.riskMonths = WINDOW_TO_MONTHS[filter.window];
  }

  const bizIds = (filter.bizTypeIds || []).map(Number).filter((n) => Number.isFinite(n) && n > 0);
  if (bizIds.length) q.businessCategoryIds = bizIds;

  // false 與「不送」在後端可能不同義,所以只在 true 時送
  if (filter.onlyKeyBusiness) q.onlyKeyBusiness = true;

  return q;
}

/**
 * 硬體刪除前檢查（UAT #30）— 比照 rackBlockers 的介面。
 *
 * CanDelete 回傳 HardwareDeletionCheckResultDto:
 *   { can_delete, software_count, sample_software_nos[] }
 *
 * ⚠️ 後端尚未實作;前端先搭好,呼叫端 catch 後降級為「不擋刪除」。
 * 等後端補上 CanDelete 端點,這裡會自動生效。
 */
export function hardwareBlockers(check) {
  if (!check || check.can_delete) return [];
  const n = check.software_count || 0;
  if (!n) return [];
  const sample = (check.sample_software_nos || []).join("、");
  return [{
    type: "軟體",
    items: sample
      ? `${sample}${n > (check.sample_software_nos || []).length ? ` 等 ${n} 套` : `（${n} 套）`}`
      : `${n} 套`,
    to: "/dck/assets/software",
  }];
}

const num = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? 0 : Number(v));
/** 軟體關聯陣列 → 表單文字框的顯示字串(名稱缺就用軟體編號;都缺的略過) */
const softwareRelationText = (list) => (list || [])
  .map((x) => (x && (x.name || x.software_no)) || "")
  .filter(Boolean)
  .join("、");
/** id 陣列:只留有限數字,缺就空陣列 */
const idList = (list) => (Array.isArray(list) ? list.map(Number).filter((n) => Number.isFinite(n)) : []);
/**
 * RAM／硬碟的單位換算。
 *
 * 表單那兩格旁邊有 GB／TB 下拉，契約卻把單位寫死在欄位名裡
 * （`ram_gb`／`disk_capacity_tb`）。不換算的話「256 TB RAM」會被存成 256 GB ——
 * 數字還在、單位掉了，畫面上完全看不出來。
 *
 * ⚠️ 一律以 **1024** 換算。硬碟廠商慣用 1000，但這兩格在表單上就是一個下拉切換，
 * 使用者輸入 1TB 期待的是「和 1024GB 同一件事」。契約沒有講，這是前端的假設，
 * 後端若採 1000 要回頭改（票 42 已記）。
 */
const gbOf = (value, unit) => (unit === "TB" ? num(value) * 1024 : num(value));
const tbOf = (value, unit) => (unit === "GB" ? num(value) / 1024 : num(value));
/** 後端回 date-time,畫面與 DateField 只吃 YYYY-MM-DD */
const dateOnly = (s) => (typeof s === "string" && s.length >= 10 ? s.slice(0, 10) : s || "");
/** 命中數:0 要留成 0（掃過沒命中），缺欄才是 null（不知道） */
const countOrNull = (n) => (n === null || n === undefined ? null : Number(n));
